import React, {useReducer, PropsWithChildren} from 'react';
import {reducer} from 'lib';
import type { Episode, EpisodeMeta } from 'types';
import { useEffect } from 'react';
import { DateTime } from 'luxon';
import XMLParser from 'react-xml-parser';
import { cdnImage } from 'components';
import {Parser} from 'html-to-react';
import { episodeMeta } from 'data';
import { useRefs } from 'hooks';
import { useCollectionOnce } from 'react-firebase-hooks/firestore';
const parser = Parser();
 
const DEFAULT_PROFILE_IMAGE = 'nerdherfers-profile-v2.jpg';

const getUrlId = (season: number, episode: number, title: string) => 
    `s${season}-ep${episode}-${title.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim().replace(/\s+/g, '-')}`;

export interface IFeedContext {
  feed: Episode[];
  getEpisodeBySlug: (slug: string) => Episode | undefined;
  seasons: number;
}

export const FeedContext =
  React.createContext<Partial<IFeedContext>>({});

export const FeedProvider = ({children}: PropsWithChildren) => {

    const [{
        feed = [],
        feedData = [],
        seasons = 1,
    }, dispatch] = useReducer(reducer, {});

    const { episodeMetasRef } = useRefs();

    const [metas] = useCollectionOnce(
        episodeMetasRef,
    )

    useEffect(() => {
        (async () => {
            await fetch('https://anchor.fm/s/106608ba8/podcast/rss')
                .then(res => res.text())
                .then(text => {
                    return new XMLParser().parseFromString(text)
                })
                .then(async json => {
                    const items = await json.getElementsByTagName('item');
                    dispatch({ feedData: items });
                });
        })();
    },[]);

    useEffect(() => {
        (async () => {
            const result: Episode[] = [];
            if (!feedData?.length) return result;
            const seasonsArr: number[] = [];
            for (const item of feedData) {
                const guid = await item.getElementsByTagName('guid')?.[0]?.value?.split('/').pop();
                const title = await item.getElementsByTagName('title')?.[0].value?.replace(/>/gi, '')?.trim();
                let image = (await item.getElementsByTagName('itunes:image')?.[0]?.attributes?.href);
                if (!image) image = cdnImage(DEFAULT_PROFILE_IMAGE);
                const link = await item.getElementsByTagName('link')?.[0].value;
                let text = parser.parse((await item.getElementsByTagName('itunes:summary')?.[0])?.value.replace(/\n/gi, '<br/>').replace(/>$/, ''));
                if (Array.isArray(text)) text = text.filter(s => typeof s === 'string').join(' ');
                const d = await item.getElementsByTagName('itunes:duration')?.[0].value;
                let season = parseInt(await item.getElementsByTagName('itunes:season')?.[0]?.value || '0') || 0;
                let episode = parseInt(await item.getElementsByTagName('itunes:episode')?.[0]?.value || '0') || 0;
                let cleanTitle = title.split(':').pop().trim();
                if (!season || !episode) {
                    // Pattern match title Sn Epn
                    let match = title.match(/S(\d+)\sEp(\d+)/);
                    if (match) {
                        season = parseInt(match[1], 10);
                        episode = parseInt(match[2], 10);
                        cleanTitle = title.replace(/S(\d+)\sEp(\d+)\s*:?\s*/, '').trim();
                    } else {
                        if (!match) 
                            match = title.match(/Ep(isode)?\s(\d+)/)?.filter((m: string) => m !== undefined);
                        if (match) {
                            season = 1;
                            episode = parseInt(match.find((m: string) => m !== undefined && parseInt(m, 10) > 0) ?? '0', 10);
                            cleanTitle = title.replace(/Ep(isode)?\s(\d+)\s*:?\s*/, '').trim();
                        }
                    }
                }
                if (season && !seasonsArr.includes(season)) seasonsArr.push(season)
                const trackedLink = await item.getElementsByTagName('enclosure')?.[0]?.attributes?.url;
                let duration = 0;
                if (d) {
                    let [hr, min, sec] = d.split(':');
                    if (!sec) {
                        sec = min;
                        min = hr;
                        hr = 0;
                    }
                    duration = (parseInt(hr) * 60) + parseInt(min);
                }
                const dt = await item.getElementsByTagName('pubDate')?.[0].value;
                const published = dt ? DateTime.fromJSDate(new Date(dt)) : null;
                const id = getUrlId(season, episode, cleanTitle);
                let meta = episodeMeta.find((m: EpisodeMeta) => m.season === season && m.episode === episode);
                if (!meta) {
                    const doc = metas?.docs?.find(m => m.get('season') === season && m.get('episode') === episode);
                    if (doc?.exists()) 
                        meta = Object.assign({}, { ...doc.data(), id: doc.id });
                }
                result.push({
                    id,
                    guid,
                    title,
                    image,
                    link,
                    text,
                    published,
                    duration,
                    season,
                    episode,
                    trackedLink,
                    meta,
                });
            }
            dispatch({ feed: result, seasons: seasonsArr.length });
        })();
    },[feedData,metas])

    const getEpisodeBySlug = (slug: string) => feed?.find((e: Episode) => e.id === slug);

    return (
        <FeedContext.Provider value={{ feed, getEpisodeBySlug, seasons }}>
            {children}
        </FeedContext.Provider>
    );
};
